import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { CalendarDays, LogOut, Menu, ShoppingCart, User, Users, X } from 'lucide-react';
import { auth } from '@/store/authStore';
import { clubsBookingApi } from '@/api/clubs-booking';
import { getPublicCartTotalQuantity } from '@/utils/publicCart';

type ParentPortalShellProps = {
  title?: string;
  subtitle?: string;
  showHeaderCard?: boolean;
  children: ReactNode;
};

const navItems = [
  { to: '/my-bookings', label: 'My bookings', icon: CalendarDays },
  { to: '/my-children', label: 'My children', icon: Users },
  { to: '/my-account', label: 'My profile', icon: User },
];

const ParentPortalShell = ({ title, subtitle, showHeaderCard = true, children }: ParentPortalShellProps) => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [parentName, setParentName] = useState('');

  useEffect(() => {
    const updateCart = () => setCartCount(getPublicCartTotalQuantity());
    updateCart();
    window.addEventListener('storage', updateCart);
    window.addEventListener('focus', updateCart);
    window.addEventListener('clubsbooking-cart-updated', updateCart);
    return () => {
      window.removeEventListener('storage', updateCart);
      window.removeEventListener('focus', updateCart);
      window.removeEventListener('clubsbooking-cart-updated', updateCart);
    };
  }, []);

  useEffect(() => {
    const user = auth.getUser();
    setParentName(user.name || 'Parent');
  }, []);

  const logout = () => {
    auth.clearAuth();
    clubsBookingApi.clearAuthToken();
    navigate('/parent/login');
  };

  const renderNav = (isMobile = false) => (
    <>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => isMobile && setDrawerOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition ${
                isActive ? 'bg-white/20 font-medium text-white' : 'text-white/85 hover:bg-white/10'
              }`
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
        <Link
          to="/cart"
          onClick={() => isMobile && setDrawerOpen(false)}
          className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm text-white/85 hover:bg-white/10"
        >
          <ShoppingCart className="h-4 w-4" />
          Cart ({cartCount})
        </Link>
      </nav>
      <div className="mt-auto border-t border-white/15 px-3 pb-5 pt-4">
        <button
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-white/85 hover:bg-white/10"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <main className="min-h-[100dvh] bg-[#f3f4f8] text-[#242a3a]">
      <div className="w-full lg:pl-64">
        {drawerOpen && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setDrawerOpen(false)} />}

        <aside className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-[#17754D] text-white transition-transform duration-200 lg:hidden ${drawerOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'}`}>
          <div className="flex items-center justify-between border-b border-white/15 px-5 py-4">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Session Monkey" className="h-7 w-7 object-contain" />
              <p className="whitespace-nowrap text-base font-semibold">Session Monkey</p>
            </div>
            <button onClick={() => setDrawerOpen(false)} className="rounded-md p-1 hover:bg-white/10" aria-label="Close menu">
              <X className="h-5 w-5" />
            </button>
          </div>
          {renderNav(true)}
        </aside>

        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 bg-[#17754D] text-white lg:flex lg:flex-col">
          <div className="border-b border-white/15 px-6 py-6">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Session Monkey" className="h-8 w-8 object-contain" />
              <p className="whitespace-nowrap text-base font-semibold">Session Monkey</p>
            </div>
            <p className="mt-1 truncate text-xs text-white/70">{parentName}</p>
          </div>
          {renderNav()}
        </aside>

        <section className="min-w-0 flex-1 px-3 pb-8 pt-4 sm:px-4 md:px-8 md:pt-6">
          <div className="mb-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2.5">
            <button onClick={() => setDrawerOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 lg:hidden">
              <Menu className="h-4 w-4" />
              Menu
            </button>
            <div className="hidden lg:block text-sm text-slate-600">Logged in as parent</div>
            <Link to="/cart" className="inline-flex items-center gap-2 rounded-lg border border-[#D8F1E2] bg-[#F3FBF7] px-3 py-2 text-sm font-medium text-[#1F8F5F]">
              <ShoppingCart className="h-4 w-4" />
              <span className="hidden sm:inline">Cart</span> ({cartCount})
            </Link>
          </div>

          {showHeaderCard && (
            <header className="mb-5 rounded-2xl border border-slate-200 bg-white px-5 py-4">
              {title && <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl">{title}</h1>}
              {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
            </header>
          )}

          {children}
        </section>
      </div>
    </main>
  );
};

export default ParentPortalShell;


